import { Alert, Button, Form, InputNumber, Select, Space, Switch, Typography, message } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { TbFolderCog } from 'react-icons/tb'

import { withDiskFeature, formatBytes } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { Panel } from '@/Ship/Components/Dashboard/Overview/shared'
import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { DiskAdminShell } from '../Components/DiskAdminShell'
import { QUOTA_UNITS, formatDateTime, joinQuota, splitQuota } from '../Helpers/diskAdminFormat'
import { diskPolicyStore as store } from '../Stores/DiskPolicyStore'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { DiskAfterDeadline, DiskPolicyValues } from '../Contracts/DiskAdmin'
import type { QuotaUnit } from '../Helpers/diskAdminFormat'

import styles from '../Components/DiskAdmin.module.css'

/** Межі полів — ті самі, що перевіряє бек (`UpdateDiskPolicyAction::validate`). */
const LIMITS = {
    versionsToKeep: [1, 500],
    trashDays: [1, 365],
    inactiveDays: [1, 3650],
    sharedActiveDays: [1, 3650],
    offboardingDays: [1, 365],
} as const satisfies Partial<Record<keyof DiskPolicyValues, readonly [number, number]>>

type NumberKey = keyof typeof LIMITS

const NumberField = observer(({ name, label, extra, suffix }: { name: NumberKey; label: string; extra?: string; suffix: string }) => {
    const draft = store.draft
    const [min, max] = LIMITS[name]

    return (
        <Form.Item label={label} {...(extra ? { extra } : {})}>
            <InputNumber
                aria-label={label}
                min={min}
                max={max}
                precision={0}
                value={draft?.[name] ?? null}
                onChange={(value) => {
                    if (typeof value === 'number') store.set(name, value)
                }}
                addonAfter={suffix}
                style={{ width: 200 }}
                data-policy={name}
            />
        </Form.Item>
    )
})

const QuotaField = observer(() => {
    const draft = store.draft
    const split = splitQuota(draft?.personalQuotaBytes ?? 0)

    return (
        <Form.Item
            label="Квота «Мого диска» за замовчуванням"
            extra="Діє на особисті диски без власної квоти; власна квота диска має пріоритет."
        >
            <Space.Compact>
                <InputNumber
                    aria-label="Квота «Мого диска» за замовчуванням"
                    min={0}
                    value={split.value}
                    onChange={(value) => {
                        if (typeof value === 'number') store.set('personalQuotaBytes', joinQuota(value, split.unit))
                    }}
                    style={{ width: 140 }}
                    data-policy="personalQuotaBytes"
                />
                <Select<QuotaUnit>
                    aria-label="Одиниця квоти"
                    value={split.unit}
                    onChange={(unit) => store.set('personalQuotaBytes', joinQuota(split.value, unit))}
                    options={QUOTA_UNITS.map((unit) => ({ value: unit, label: unit }))}
                    style={{ width: 80 }}
                />
            </Space.Compact>
        </Form.Item>
    )
})

/**
 * Налаштування диска `/disk/admin/policy` (`DISK-006` §3.10, наряд Ф7):
 * квота «Мого диска» за замовчуванням, версії, кошик, пороги неактивності й
 * «спільне активно», строк рішення по диску звільненого, нагадування й дія
 * після строку. Ліміт компанії — лише читання: його задає сервер.
 *
 * PATCH шле лише змінені ключі; кожна зміна — запис «Змінено політику диска»
 * в журналі адміністратора.
 */
const PolicyPage = observer(() => {
    useEffect(() => {
        void store.load()
    }, [])

    const { policy, draft } = store

    const save = async () => {
        if (await store.save()) void message.success('Налаштування збережено')
    }

    return (
        <DiskAdminShell tab="policy" documentTitle="Налаштування — адміністрування диска" eyebrow="Правила диска для всієї компанії">
            {store.error !== null ? <Alert type="error" showIcon title={store.error} /> : null}

            <Panel
                block="disk-admin-policy"
                title="Налаштування диска"
                subtitle={
                    policy?.updatedAt
                        ? `Змінено ${formatDateTime(policy.updatedAt)}${policy.updatedBy?.name ? ` · ${policy.updatedBy.name}` : ''}`
                        : 'Діють значення за замовчуванням'
                }
                loading={store.isLoading && !policy}
                skeletonRows={10}
            >
                {policy && draft ? (
                    <Form layout="vertical" onFinish={() => void save()} data-testid="disk-admin-policy-form">
                        <Hint id="disk.admin.policy">
                            Нові строк кошика й кількість версій отримують простори, де стояло попереднє значення
                            політики; простір із власним налаштуванням його зберігає.
                        </Hint>
                        <div className={classNames(styles['form'])} style={{ marginTop: 16 }}>
                            <QuotaField />
                            <Form.Item
                                label="Ліміт диска компанії"
                                extra="Задається на сервері (тариф і місце на ньому), тут не змінюється."
                            >
                                <span className={classNames(styles['readonly'])} data-policy="tenantQuotaBytes">
                                    {policy.tenantQuotaBytes !== null ? formatBytes(policy.tenantQuotaBytes) : 'не задано'}
                                </span>
                            </Form.Item>
                            <NumberField name="versionsToKeep" label="Версій файлу тримати" suffix="версій" />
                            <NumberField
                                name="trashDays"
                                label="Строк кошика"
                                suffix="днів"
                                extra="Після строку файл видаляється з кошика остаточно."
                            />
                            <NumberField
                                name="inactiveDays"
                                label="Поріг «неактивний»"
                                suffix="днів"
                                extra="Чип «Неактивні» в списку дисків."
                            />
                            <NumberField
                                name="sharedActiveDays"
                                label="Вікно «спільне використовується»"
                                suffix="днів"
                                extra="Спільний файл звільненого, який колеги відкривали за цей час, — «використовується»."
                            />
                            <NumberField name="offboardingDays" label="Строк рішення по диску звільненого" suffix="днів" />
                            <Form.Item label="Нагадування" extra="На які дні від деактивації нагадати адміністраторам.">
                                <Select<number[]>
                                    mode="tags"
                                    aria-label="Нагадування"
                                    value={draft.reminderDays}
                                    onChange={(values) =>
                                        store.set(
                                            'reminderDays',
                                            [...new Set(values.map(Number).filter((day) => Number.isInteger(day) && day > 0))].sort(
                                                (a, b) => a - b
                                            )
                                        )
                                    }
                                    options={draft.reminderDays.map((day) => ({ value: day, label: `${day}-й день` }))}
                                    tokenSeparators={[',', ' ']}
                                    style={{ width: '100%', maxWidth: 320 }}
                                    data-policy="reminderDays"
                                />
                            </Form.Item>
                            <Form.Item label="Дія після строку">
                                <Select<DiskAfterDeadline>
                                    aria-label="Дія після строку"
                                    value={draft.afterDeadline}
                                    onChange={(value) => store.set('afterDeadline', value)}
                                    options={policy.afterDeadlineOptions}
                                    style={{ width: '100%', maxWidth: 320 }}
                                    data-policy="afterDeadline"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Вимикати публічні посилання звільненого одразу"
                                extra="Діятиме, коли на диску з’являться публічні посилання."
                            >
                                <Switch
                                    checked={draft.suspendPublicLinks}
                                    onChange={(checked) => store.set('suspendPublicLinks', checked)}
                                />
                            </Form.Item>
                        </div>
                        {store.saveError !== null ? (
                            <Alert type="error" showIcon title={store.saveError} style={{ marginBottom: 12 }} />
                        ) : null}
                        <div className={classNames(styles['formActions'])}>
                            <Button type="primary" htmlType="submit" loading={store.isSaving} disabled={!store.isDirty}>
                                Зберегти
                            </Button>
                            <Button onClick={() => store.reset()} disabled={!store.isDirty || store.isSaving}>
                                Скасувати зміни
                            </Button>
                            {store.isDirty ? (
                                <Typography.Text type="secondary">Є незбережені зміни</Typography.Text>
                            ) : null}
                        </div>
                    </Form>
                ) : null}
            </Panel>
        </DiskAdminShell>
    )
})

export const pageConfig: IPageConfig = {
    key: 'Disk.AdminPolicy',
    route: '/disk/admin/policy',
    title: 'Налаштування — адміністрування диска',
    icon: <TbFolderCog />,
}

export default withDiskFeature(PolicyPage)
